// Entry point: boot → /api/auth/me → login / setup / forced password change, or the app:
// /api/bootstrap → 3D scene + DOM layers (top bar, elevator, cards, name tags, connector lines,
// task panel) → SSE → hash routing. See docs/SPEC.md §11 and docs/UI-CONTRACT.md.
import { api, setCsrf, onUnauthorized, connectEvents, onServerBuild, onStaleClient, pendingWrites } from './api.js';
import {
  createAppStore, parseRoute, floorHash, floorOfRoute, sortedFloors, computeStats, normalizeStatus, floorDepartments, can, studioHash,
} from './store.js';
import * as dom from './dom.js';
import { t } from './i18n/fr.js';
import * as format from './format.js';
import { createScene } from './scene/index.js';
import { renderAuthScreen } from './ui/login.js';
import { createOverlay } from './ui/overlay.js';
import { createToaster } from './ui/toast.js';
import { createKeys, openShortcutsHelp } from './ui/keys.js';
import { createTopbar } from './ui/topbar.js';
import { iconForConnector } from './ui/connectoricons.js';
import { openUserMenu } from './ui/usermenu.js';
import { createElevator, createSceneControls } from './ui/elevator.js';
import { createDeptCards } from './ui/deptcards.js';
import { openFloorMenu, openBuildingMenu, openDeptMenu, openNewFloorDialog, openNewDeptDialog } from './ui/officeactions.js';
import { createNameTags } from './ui/nametags.js';
import { createConnectorLines, matchConnector, deptConnectorLinks } from './ui/connectorlines.js';
import { createTaskPanel } from './ui/taskpanel.js';
import { createConnectionBanner } from './ui/connbanner.js';
import { createUpdateNotice, reloadIfStale } from './ui/updatenotice.js';
import { liveReloadWaits } from './ui/studio/item-parts.js';
import { createNotificationCenter } from './ui/notifications.js';
import { floorNumber } from './scene/layout.js';
import * as panels from './ui/extensions.js';
import { clearTriggerSamples } from './ui/automations/widgets.js';
import { isOwnNotify } from './ui/suggestions/model.js';
import { liveConnectorOf, connectorsOf, withConnector, withoutConnector, libraryAccess, LIBRARY_BASE } from './ui/myconnectors/model.js';

const { h, svg, clear, button, emptyState, statusDot } = dom;
const THEME_KEY = 'mo.theme';
const FLOOR_KEY = 'mo.floor';
const STATS_FALLBACK_MS = 60000;

const root = document.getElementById('root');
const store = createAppStore();
const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
let app = null;
let authScreen = null;

// ---------------------------------------------------------------------------------------------
// Small helpers

function storageGet(key) {
  try { return window.localStorage.getItem(key); } catch { return null; }
}

function storageSet(key, value) {
  try {
    if (value == null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch { /* private mode: ignore */ }
}

function toMs(v) {
  if (typeof v === 'number') return v;
  const n = Date.parse(v);
  return Number.isFinite(n) ? n : null;
}

// ---------------------------------------------------------------------------------------------
// Theme

const THEME_COLORS = { light: '#F4F1EA', dark: '#0E131D' };

function resolveTheme(pref) {
  return pref === 'dark' || (pref !== 'light' && darkQuery.matches) ? 'dark' : 'light';
}

function applyTheme(pref) {
  const theme = resolveTheme(pref);
  const el = document.documentElement;
  if (pref === 'light' || pref === 'dark') el.setAttribute('data-theme', pref);
  else el.removeAttribute('data-theme');
  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    if (pref === 'light' || pref === 'dark') meta.setAttribute('content', THEME_COLORS[theme]);
    else meta.setAttribute('content', meta.getAttribute('media')?.includes('dark') ? THEME_COLORS.dark : THEME_COLORS.light);
  }
  store.setUi({ theme, themePref: pref });
  app?.sceneCall('setTheme', theme);
}

function toggleTheme() {
  const next = store.get().ui.theme === 'dark' ? 'light' : 'dark';
  storageSet(THEME_KEY, next);
  applyTheme(next);
}

darkQuery.addEventListener?.('change', () => { if (store.get().ui.themePref === 'system') applyTheme('system'); });
motionQuery.addEventListener?.('change', () => {
  store.setUi({ reducedMotion: motionQuery.matches });
  document.documentElement.classList.toggle('reduced-motion', motionQuery.matches);
  app?.sceneCall('setReducedMotion', motionQuery.matches); // optional scene API
});

// ---------------------------------------------------------------------------------------------
// Boot and authentication screens

function destroyAuth() {
  authScreen?.destroy();
  authScreen = null;
}

function destroyApp() {
  app?.destroy();
  app = null;
}

function showSplash() {
  clear(root);
  root.className = 'boot';
  root.appendChild(h('div', { class: 'boot-splash', role: 'status' },
    h('p', { class: 'boot-wordmark' }, t('app.wordmark')), dom.spinner(t('common.loading'))));
}

function showFatal(err, retry = boot) {
  destroyApp();
  destroyAuth();
  clear(root);
  root.className = 'boot';
  root.appendChild(h('div', { class: 'boot-splash boot-error', role: 'alert' },
    h('p', { class: 'boot-wordmark' }, t('app.wordmark')),
    h('p', { class: 'boot-title' }, t('app.bootError')),
    h('p', { class: 'boot-text' }, err?.message || t('app.bootErrorHint')),
    h('p', { class: 'boot-text muted' }, t('app.bootErrorHint')),
    button(t('common.retry'), { variant: 'primary', onClick: () => retry() })));
}

function showAuth(mode, opts = {}) {
  destroyApp();
  destroyAuth();
  store.resetSession(); // the next person to sign in must not see this session's data
  clearTriggerSamples(); // nor the n8n sample payloads (customer data) kept in this browser
  const titles = { login: 'login.title', setup: 'setup.title', change: 'change.title' };
  document.title = `${t(titles[mode] || 'login.title')} · ${t('app.name')}`;
  authScreen = renderAuthScreen(root, {
    mode, api, ...opts,
    onDone: (res) => {
      if (location.hash.startsWith('#/setup')) history.replaceState(null, '', '#/');
      if (res?.user?.mustChangePassword) { showAuth('change', { me: res.user }); return; }
      boot();
    },
    onLogout: logout,
    onSwitch: (next) => {
      if (next === 'setup') { history.replaceState(null, '', '#/setup'); showAuth('setup', {}); } else {
        history.replaceState(null, '', '#/');
        showAuth('login', { setupRequired: true });
      }
    },
  });
}

async function logout() {
  try { await api.post('/api/auth/logout'); } catch { /* the session may already be gone */ }
  setCsrf('');
  store.set({ me: null });
  history.replaceState(null, '', '#/');
  showAuth('login', { notice: t('logout.done') });
}

/** Resolves the session and shows the right screen. */
async function boot(notice) {
  destroyApp();
  destroyAuth();
  showSplash();
  let me;
  try {
    me = await api.get('/api/auth/me');
  } catch (err) {
    showFatal(err);
    return;
  }
  if (me?.csrf) setCsrf(me.csrf);
  const route = parseRoute(location.hash);
  if (!me?.user) {
    if (me?.setupRequired) { showAuth('setup', { token: route.token }); return; }
    showAuth('login', { notice: route.name === 'setup' ? t('setup.notRequired') : notice });
    return;
  }
  if (me.user.mustChangePassword) { showAuth('change', { me: me.user }); return; }
  store.set({ me: me.user, csrf: me.csrf || '' });
  if (route.name === 'setup') history.replaceState(null, '', '#/');
  await startApp();
}

onUnauthorized(() => {
  if (!app) return;
  showAuth('login', { notice: t('app.sessionExpired') });
});

// The web build the server serves, from any API response (also on the login screen): a page that
// stayed open through a deploy learns there is a new version (web/ui/updatenotice.js).
onServerBuild((build) => store.set({ serverBuild: build }));

// ---------------------------------------------------------------------------------------------
// Bootstrap data

function applyBootstrap(b) {
  const s = store.get();
  if (!b) { store.set({ degraded: true }); return; }
  if (b.csrf) setCsrf(b.csrf);
  // null: the server does not know its build right now (a deploy in progress): keep what we have.
  if (typeof b.office?.build === 'string') store.set({ serverBuild: b.office.build });
  const statusMap = new Map();
  for (const a of b.agents || []) statusMap.set(a.id, normalizeStatus(a.status));
  store.batch(() => {
    store.set({
      me: b.me || s.me,
      csrf: b.csrf || s.csrf,
      office: {
        ...s.office, ...(b.office || {}),
        floors: Array.isArray(b.floors) ? b.floors : [],
        departments: Array.isArray(b.departments) ? b.departments : [],
        agents: Array.isArray(b.agents) ? b.agents : [],
      },
      agentStatus: statusMap,
      connectors: Array.isArray(b.connectors) ? b.connectors : [],
      engines: b.engines || {},
      counts: { awaitingApproval: 0, running: 0, queued: 0, ...(b.counts || {}) },
      features: { atelier: false, ...(b.features || {}) },
      degraded: false,
    });
  });
}

async function startApp() {
  let data = null;
  try {
    data = await api.get('/api/bootstrap');
  } catch (err) {
    // The app is not mounted yet, so the global onUnauthorized hook ignores this 401: the session
    // ended between /api/auth/me and /api/bootstrap (expired, revoked, user disabled).
    if (err.status === 401) { setCsrf(''); showAuth('login', { notice: t('app.sessionExpired') }); return; }
    if (err.status !== 404) { showFatal(err, startApp); return; }
  }
  // A page older than the build served (a tab left on the login screen through a deploy): reload
  // now, before anything is mounted (once per build; the splash stays meanwhile).
  if (reloadIfStale(data?.office?.build)) return;
  applyBootstrap(data);
  destroyAuth();
  app = createApp();
  // Debug handle for `node scripts/build.js --dev` builds only (removed from production bundles).
  if (process.env.NODE_ENV !== 'production') window.__maeck = { store, app };
}

// ---------------------------------------------------------------------------------------------
// The app

const NOOP_SCENE = {
  setOffice() {}, setAgentStatus() {}, setDeptStats() {}, showFloor() {}, showBuilding() {}, focusDept() {},
  resetView() {}, zoom() {}, pulseConnector() {}, getAnchors: () => [], setTheme() {}, resize() {}, dispose() {},
};

function createApp() {
  clear(root);
  root.className = 'app-root';
  document.title = t('app.name');
  const appEl = h('div', { class: 'app', id: 'app' });
  const layersHost = h('div', { class: 'layers-host' });
  root.append(appEl, layersHost);

  const offs = [];
  const cleanups = [];
  const eventListeners = new Map();
  const overlay = createOverlay({ host: layersHost, appRoot: appEl, reducedMotion: () => motionQuery.matches });
  const toast = createToaster({ host: layersHost });
  let scene = NOOP_SCENE;
  let lines = null;
  let destroyed = false;

  function sceneCall(name, ...args) {
    try { return scene?.[name]?.(...args); } catch (err) { console.error('[scene]', name, err); return undefined; }
  }

  /** Context passed to every panel (see docs/UI-CONTRACT.md). */
  const ctx = {
    api, store, t, toast, overlay, navigate, dom, format,
    get scene() { return scene; },
    keys: null,
    events: {
      on(type, fn) {
        if (!eventListeners.has(type)) eventListeners.set(type, new Set());
        eventListeners.get(type).add(fn);
        return () => eventListeners.get(type)?.delete(fn);
      },
    },
    can: (need, scope) => can(store.get(), need, scope),
    refresh: {
      office: () => refreshBootstrap(0), tasks: (opts) => loadTasks(opts), stats: () => loadStats(), suggestions: () => loadSuggestions(),
      libraryConnectors: () => loadLibraryConnectors(),
    },
  };

  // --- DOM skeleton -----------------------------------------------------------------------------
  const sceneHost = h('div', { class: 'scene-host', 'aria-hidden': 'true' });
  const tagsLayer = h('div', { class: 'scene-layer tags-layer' });
  const cardsLayer = h('div', { class: 'scene-layer cards-layer' });
  const sceneEmpty = h('div', { class: 'scene-empty', hidden: true });
  const controls = createSceneControls(ctx);
  // « À régler » : the bubble sits on top of the zoom buttons (bottom left; in their row on a phone).
  const notices = createNotificationCenter(ctx);
  controls.el.prepend(notices.el);
  cleanups.push(() => notices.destroy());
  const sceneWrap = h('main', { class: 'scene-wrap', id: 'main', 'aria-label': t('app.sceneLabel') },
    sceneHost, tagsLayer, cardsLayer, sceneEmpty, controls.el);
  const linesSvg = svg('svg', { class: 'lines-layer', 'aria-hidden': 'true', focusable: 'false' });

  const topbar = createTopbar(ctx, {
    onApprovals: () => navigate('#/approvals'),
    onCalendar: () => navigate('#/calendar'),
    onAutomations: () => navigate('#/automations'),
    onStudio: (floorId) => navigate(studioHash(store.get().office.floors, floorId)),
    onUserMenu: (anchor) => openUserMenu(ctx, anchor, {
      onToggleTheme: toggleTheme, onLogout: logout, onShortcuts: () => openShortcutsHelp(overlay),
    }),
    onConnector: (c, chip) => showConnector(c, chip),
    onLayout: () => lines?.layout(),
    onFloorMenu: (anchor, floorId) => openFloorMenu(ctx, anchor, floorId),
    onBuildingMenu: (anchor) => openBuildingMenu(ctx, anchor),
  });
  const connBanner = createConnectionBanner(ctx, { host: appEl });
  cleanups.push(() => connBanner.destroy());
  // « Une nouvelle version du bureau est en ligne. » [Recharger] (by itself only in a hidden tab
  // with nothing in progress: no write on its way, no layer open, no upload or video playing).
  // Next to the layers, not in the app: a sheet (studio, administration…) would cover it and a
  // drawer or a dialog would make it inert; it stays above them, below the toasts.
  const updates = createUpdateNotice(ctx, { host: layersHost, pendingWrites, isBusy: () => liveReloadWaits(document.body) });
  cleanups.push(() => updates.destroy());
  cleanups.push(onStaleClient(() => updates.markStale()));
  const elevator = createElevator(ctx, {
    onSelectFloor: (id) => navigate(floorLink(id)),
    onBuilding: () => navigate('#/building'),
  });
  const panel = createTaskPanel(ctx, {
    onOpenTask: (id) => navigate(`#/task/${encodeURIComponent(id)}`),
    onOpenApprovals: () => navigate('#/approvals'),
    onOpenBoard: () => navigate('#/taches'),
  });
  // The skip link must not change location.hash: "#tasks" would go through the router (unknown
  // route → default floor). It moves focus to the task list instead.
  const skipLink = h('a', {
    class: 'skip-link', href: '#tasks',
    onClick: (e) => { e.preventDefault(); panel.focusList(); },
  }, t('app.skip'));
  appEl.append(skipLink, topbar.el, elevator.el, sceneWrap, linesSvg, panel.el);

  // --- scene ------------------------------------------------------------------------------------
  const setHover = (target) => store.setUi({ hoverId: target && target.id ? { kind: target.kind, id: target.id } : null });
  const toggleDeptFilter = (id) => {
    const cur = store.get().ui.deptFilter;
    store.setUi({ deptFilter: cur === id ? null : id });
  };
  // Anchors are pushed by the scene after each render (onAnchors, an optional extension); a slow
  // poll of getAnchors() covers scenes without it and idle periods.
  let lastAnchors = [];
  let lastPushAt = 0;
  let cards = null;
  let tags = null;
  function applyAnchors(anchors) {
    lastAnchors = Array.isArray(anchors) ? anchors : [];
    cards?.update(lastAnchors);
    tags?.update(lastAnchors);
    lines?.update(lastAnchors);
  }
  try {
    scene = createScene(sceneHost, {
      theme: store.get().ui.theme,
      reducedMotion: motionQuery.matches,
      onAgentClick: (id) => navigate(`#/agent/${encodeURIComponent(id)}`),
      onDeptClick: (id) => toggleDeptFilter(id),
      onFloorClick: (id) => navigate(floorLink(id)),
      onHover: setHover,
      onAnchors: (anchors) => {
        if (destroyed) return;
        lastPushAt = performance.now();
        applyAnchors(anchors);
      },
    }) || NOOP_SCENE;
  } catch (err) {
    console.error('[scene] creation failed', err);
    scene = NOOP_SCENE;
  }

  cards = createDeptCards(ctx, {
    layer: cardsLayer,
    getSafeRight: () => sceneHost.clientWidth - (insets.right || 0),
    onMetrics: (size) => sceneCall('setCardMetrics', size), // optional scene API
    onDeptClick: toggleDeptFilter,
    onDeptFocus: (id) => sceneCall('focusDept', id),
    onFloorClick: (id) => navigate(floorLink(id)),
    onAgentClick: (id) => navigate(`#/agent/${encodeURIComponent(id)}`),
    onDeptMenu: (id, anchor) => openDeptMenu(ctx, anchor, id),
    onDeptStudio: (id) => {
      const d = store.get().office.departments.find((x) => x.id === id);
      if (d) navigate(studioHash(store.get().office.floors, d.floorId, d.id));
    },
    onHover: (target) => {
      setHover(target);
      if (!target || target.kind === 'dept') sceneCall('highlightDept', target ? target.id : null); // optional scene API
    },
  });
  tags = createNameTags(ctx, { layer: tagsLayer, onAgentClick: (id) => navigate(`#/agent/${encodeURIComponent(id)}`), onHover: setHover });
  lines = createConnectorLines(ctx, {
    svgEl: linesSvg,
    getChipRects: () => topbar.chipRects(),
    getOrigin: () => { const r = sceneHost.getBoundingClientRect(); return { left: r.left, top: r.top }; },
  });

  function pushOffice() {
    const { floors, departments, agents } = store.get().office;
    sceneCall('setOffice', { floors, departments, agents });
    for (const [id, st] of store.get().agentStatus) sceneCall('setAgentStatus', id, st);
  }

  function pushStats() {
    const st = store.get().stats?.departments || {};
    for (const d of store.get().office.departments) {
      sceneCall('setDeptStats', d.id, { doing: 0, waiting: 0, next: 0, doneToday: 0, ...(st[d.id] || {}) });
    }
  }

  function renderSceneEmpty() {
    const s = store.get();
    const floors = s.office.floors;
    const isAdmin = s.me?.role === 'admin';
    let node = null;
    if (!floors.length) {
      node = emptyState({ icon: 'building', title: t('app.noFloors.title'), text: isAdmin ? t('app.noFloors.admin') : t('app.noFloors.text'),
        action: isAdmin ? button(t('oa.floor.new'), { variant: 'primary', icon: 'plus', onClick: () => openNewFloorDialog(ctx) }) : null });
    } else if (s.ui.view !== 'building' && s.ui.floorId && !floorDepartments(s.office.departments, s.ui.floorId).length) {
      const floorId = s.ui.floorId;
      node = emptyState({ icon: 'layers', title: t('app.emptyFloor.title'), text: isAdmin ? t('app.emptyFloor.admin') : t('app.emptyFloor.text'),
        action: isAdmin ? button(t('oa.dept.new'), { variant: 'primary', icon: 'plus', onClick: () => openNewDeptDialog(ctx, floorId) }) : null });
    }
    sceneEmpty.hidden = !node;
    sceneEmpty.replaceChildren(...(node ? [node] : []));
  }

  // --- resize handling and view insets -------------------------------------------------------------
  // The elevator and the task panel float over the scene (the bottom sheet and the zoom buttons on a
  // phone): tell the scene which parts are covered so it frames the floor in the free area
  // (setViewInsets, an optional scene extension). Phones get compact department pills.
  let lastInsets = '';
  let insets = { top: 0, right: 0, bottom: 0, left: 0 };
  const mobileQuery = window.matchMedia('(max-width: 767px)');
  function computeInsets() {
    const host = sceneHost.getBoundingClientRect();
    const ins = { top: 0, right: 0, bottom: 0, left: 0 };
    if (!host.width || !host.height) return ins;
    const p = panel.el.getBoundingClientRect();
    if (mobileQuery.matches) {
      // An expanded sheet covers nearly the whole scene: keep the framing of the resting sheet.
      if (panel.el.classList.contains('is-expanded') && lastInsets) return insets;
      const sheetTop = p.height && p.top < host.bottom ? p.top : host.bottom;
      const c = controls.el.getBoundingClientRect();
      // The zoom buttons sit in a row just above the sheet: the floor stays above them.
      const bottomEdge = c.height && c.top < sheetTop ? Math.min(sheetTop, c.top) : sheetTop;
      ins.bottom = Math.max(0, Math.round(host.bottom - bottomEdge + 4));
    } else {
      const e = elevator.el.getBoundingClientRect();
      if (e.width && e.right > host.left) ins.left = Math.round(e.right - host.left + 12);
      if (p.width && p.left < host.right && !panel.el.classList.contains('is-collapsed')) ins.right = Math.round(host.right - p.left + 12);
      ins.top = 10;
      ins.bottom = 10;
    }
    return ins;
  }
  function syncLayout() {
    sceneCall('resize');
    sceneCall('setCardMetrics', { compact: mobileQuery.matches }); // optional scene API
    const ins = computeInsets();
    const key = JSON.stringify(ins);
    if (key !== lastInsets) { lastInsets = key; insets = ins; sceneCall('setViewInsets', ins); }
    lines?.layout();
    lines?.update(lastAnchors);
  }
  let layoutRaf = 0;
  const scheduleLayout = () => { if (!layoutRaf) layoutRaf = requestAnimationFrame(() => { layoutRaf = 0; if (!destroyed) syncLayout(); }); };
  const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(scheduleLayout) : null;
  for (const el of [sceneHost, topbar.el, panel.el, elevator.el]) ro?.observe(el);
  window.addEventListener('resize', scheduleLayout);
  cleanups.push(() => { ro?.disconnect(); window.removeEventListener('resize', scheduleLayout); cancelAnimationFrame(layoutRaf); });

  // --- data loading -------------------------------------------------------------------------------
  let statsTimer = null;
  let statsUnavailableUntil = 0;
  const floorLoadedAt = new Map();

  async function loadTasks({ floorId } = {}) {
    try {
      const list = await api.get(api.withQuery('/api/tasks', { floorId, limit: 100 }));
      if (destroyed) return;
      store.setTasks(Array.isArray(list) ? list : (Array.isArray(list?.tasks) ? list.tasks : []));
      store.set({ tasksUnavailable: false });
      if (floorId) floorLoadedAt.set(floorId, Date.now());
    } catch (err) {
      if (destroyed || err.status === 401) return;
      if (err.status === 404) store.set({ tasksUnavailable: true, tasksLoaded: true });
      else toast.error(err);
    }
    scheduleStats(0);
  }

  async function loadStats() {
    if (Date.now() < statsUnavailableUntil) { store.set({ stats: computeStats(store.get().tasks.values()) }); return; }
    try {
      const st = await api.get('/api/stats');
      if (destroyed) return;
      store.set({ stats: { departments: st?.departments || {}, floors: st?.floors || {}, source: 'server' } });
    } catch (err) {
      if (destroyed || err.status === 401) return;
      if (err.status === 404) statsUnavailableUntil = Date.now() + STATS_FALLBACK_MS;
      store.set({ stats: computeStats(store.get().tasks.values()) });
    }
  }

  /**
   * Every routine the person may see (department cards: « 1 routine »), kept current by the
   * `routine` / `routine.removed` events.
   */
  let routinesSeq = 0;
  async function loadRoutines() {
    const seq = ++routinesSeq;
    try {
      const res = await api.get('/api/routines');
      if (destroyed || seq !== routinesSeq) return;
      store.setRoutines(Array.isArray(res) ? res : (Array.isArray(res?.routines) ? res.routines : []));
    } catch (err) {
      if (destroyed || seq !== routinesSeq || err.status === 401) return;
      store.set({ routinesLoaded: err.status === 404 });
    }
  }

  /**
   * The improvement suggestions (all of them for an admin, one's own otherwise): the admin's badge
   * on the top-bar SUGGESTIONS button and the panel, kept current by the `suggestion` /
   * `suggestion.removed` events. A server without the module (404) leaves the panel « Bientôt disponible ».
   */
  let suggestionsSeq = 0;
  async function loadSuggestions() {
    const seq = ++suggestionsSeq;
    try {
      const res = await api.get('/api/suggestions');
      if (destroyed || seq !== suggestionsSeq) return;
      store.setSuggestions(Array.isArray(res) ? res : (Array.isArray(res?.suggestions) ? res.suggestions : []));
    } catch (err) {
      if (destroyed || seq !== suggestionsSeq || err.status === 401) return;
      if (err.status === 404) store.set({ suggestions: [], suggestionsLoaded: true, suggestionsUnavailable: true });
    }
  }

  /**
   * The office's connector library as this person sees it (the user-menu hint counts the ones he
   * may fix that wait for it; « Connecteurs du bureau » shows them), kept current by the
   * `connector-library` / `connector-library.removed` events. Only when the server reports the
   * library on.
   */
  let librarySeq = 0;
  async function loadLibraryConnectors() {
    if (!libraryAccess(store.get()).enabled) return;
    const seq = ++librarySeq;
    try {
      const res = await api.get(LIBRARY_BASE);
      if (destroyed || seq !== librarySeq) return;
      store.set({ libraryConnectors: connectorsOf(res), libraryConnectorsLoaded: true });
    } catch (err) {
      if (destroyed || seq !== librarySeq || err.status === 401) return;
      if (err.status === 404) store.set({ libraryConnectors: [], libraryConnectorsLoaded: true });
    }
  }

  function scheduleStats(delay = 1200) {
    clearTimeout(statsTimer);
    statsTimer = setTimeout(loadStats, delay);
  }

  let bootstrapTimer = null;
  function refreshBootstrap(delay = 300) {
    clearTimeout(bootstrapTimer);
    bootstrapTimer = setTimeout(async () => {
      try {
        const data = await api.get('/api/bootstrap');
        if (destroyed) return;
        applyBootstrap(data);
        const floors = store.get().office.floors;
        const cur = store.get().ui.floorId;
        if (store.get().ui.view !== 'building' && cur && !floors.some((f) => f.id === cur)) {
          // The floor shown behind a panel was deleted (e.g. from Administration): switch the view
          // behind it without touching the route, which would close the panel.
          if (routeOverlay) {
            const f = floorOfRoute(floors, parseRoute(defaultHash()));
            if (f) showFloor(f.id); else showBuilding();
          } else navigate(defaultHash(), { replace: true });
        } else if (store.get().ui.view !== 'building' && cur && parseRoute(location.hash).name === 'floor') {
          // Floors reordered or renumbered: the address follows the floor shown (no navigation).
          const canonical = floorLink(cur);
          if (location.hash !== canonical) history.replaceState(null, '', canonical);
        }
      } catch (err) {
        if (err.status !== 401 && err.status !== 404) console.warn('[bootstrap]', err.message);
      }
    }, delay);
  }

  function resync() {
    refreshBootstrap(0);
    loadTasks();
    loadRoutines();
    loadSuggestions();
    loadLibraryConnectors();
    const f = store.get().ui.floorId;
    if (f) loadTasks({ floorId: f });
  }

  // --- live events ----------------------------------------------------------------------------------
  function showNotify(data) {
    if (!data || isOwnNotify(data, store.get().me)) return; // one's own act: no toast about it
    const isApproval = data.type === 'approval';
    const kind = ['info', 'success', 'warn', 'error'].includes(data.kind) ? data.kind
      : isApproval || data.type === 'blocked' ? 'warn' : data.type === 'failed' ? 'error' : data.type === 'done' ? 'success' : 'info';
    const message = data.message || data.text
      || (isApproval ? t('notify.approval', { title: data.title || '' })
        : data.type === 'done' ? t('notify.done', { title: data.title || '' })
          : data.type === 'failed' ? t('notify.failed', { title: data.title || '' }) : data.title)
      || t('notify.generic');
    const title = (data.message || data.text) && data.title ? data.title : undefined;
    toast(message, {
      kind, title,
      action: data.taskId ? { label: t('notify.open'), onClick: () => navigate(`#/task/${encodeURIComponent(data.taskId)}`) }
        : data.suggestionId ? { label: t('notify.open'), onClick: () => navigate('#/suggestions') }
          : typeof data.connectorId === 'string' && data.connectorId
            ? { label: t('notify.open'), onClick: () => navigate(`#/connectors/${encodeURIComponent(data.connectorId)}`) } : undefined,
    });
  }

  function handleAgent(data) {
    if (!data?.agentId) return;
    const st = normalizeStatus(data);
    store.setAgentStatus(data.agentId, st);
    sceneCall('setAgentStatus', data.agentId, st);
    if (st.activity) {
      const key = matchConnector(st.activity, store.get().connectors);
      const agent = store.get().office.agents.find((a) => a.id === data.agentId);
      if (key && agent) {
        lines?.pulse(key, agent.deptId);
        sceneCall('pulseConnector', key, agent.deptId);
      }
    }
  }

  /** The engines' status (everyone; GET /api/engines/status), for the top bar's dots. */
  let enginesSeq = 0;
  async function refreshEngines() {
    const mine = ++enginesSeq;
    try {
      const status = await api.get('/api/engines/status');
      if (mine === enginesSeq && status && typeof status === 'object' && !destroyed) store.set({ engines: status });
    } catch { /* the next bootstrap brings it */ }
  }

  function onEvent(type, data) {
    switch (type) {
      case 'hello': {
        const server = toMs(data?.serverTime);
        if (server) store.set({ serverOffset: server - Date.now() });
        // The build served (null mid-deploy: keep what we have): the update notice compares it.
        if (typeof data?.build === 'string') store.set({ serverBuild: data.build });
        break;
      }
      case 'task':
        if (data?.id) { store.applyTask(data); scheduleStats(); }
        break;
      case 'task.removed':
        if (data?.id) { store.dropTask(data.id); scheduleStats(); }
        break;
      case 'routine': {
        const r = data?.routine && typeof data.routine === 'object' ? data.routine : data;
        if (r?.id) store.upsertRoutine(r);
        break;
      }
      case 'routine.removed':
        if (data?.id) store.removeRoutine(data.id);
        break;
      case 'suggestion': {
        const sg = data?.suggestion && typeof data.suggestion === 'object' ? data.suggestion : data;
        if (sg?.id) store.upsertSuggestion(sg);
        break;
      }
      case 'suggestion.removed':
        if (data?.id) store.removeSuggestion(data.id);
        break;
      case 'connector-library': {
        // Every user receives it (never an address): this person's flags come from the copy held.
        const c = liveConnectorOf(data, store.get(), store.get().libraryConnectors);
        if (c) store.set({ libraryConnectors: withConnector(store.get().libraryConnectors, c) });
        break;
      }
      case 'connector-library.removed':
        if (data?.id) store.set({ libraryConnectors: withoutConnector(store.get().libraryConnectors, data.id) });
        break;
      case 'agent': handleAgent(data); break;
      case 'office': refreshBootstrap(); break;
      case 'notify': showNotify(data); break;
      case 'engines':
        // An engine got connected or disconnected (account, key, credits): the dots follow.
        refreshEngines();
        break;
      case 'connectors':
        if (Array.isArray(data)) store.set({ connectors: data });
        else if (Array.isArray(data?.connectors)) store.set({ connectors: data.connectors });
        else refreshBootstrap();
        break;
      case 'counts':
        if (data && typeof data === 'object') store.set({ counts: { ...store.get().counts, ...data } });
        break;
      case 'stats':
        if (data?.departments) store.set({ stats: { departments: data.departments, floors: data.floors || {}, source: 'server' } });
        break;
      default: break;
    }
    for (const fn of [...(eventListeners.get(type) || [])]) {
      try { fn(data); } catch (err) { console.error('[events]', type, err); }
    }
  }

  let wasOpen = false;
  let offline = 0;
  const events = connectEvents(onEvent, {
    onStatus: (status) => {
      store.set({ sse: status });
      if (status === 'open') {
        if (wasOpen) { resync(); toast.success(t('app.resynced'), { timeout: 2500 }); }
        wasOpen = true;
        offline = 0;
      } else if (status === 'offline') {
        offline += 1;
        if (offline >= 2) {
          api.get('/api/auth/me').then((me) => {
            if (!destroyed && !me?.user) showAuth('login', { notice: t('app.sessionExpired') });
          }).catch(() => {});
        }
      }
    },
  });
  cleanups.push(() => events.close());

  // --- routing ------------------------------------------------------------------------------------
  let routeOverlay = null;
  let firstFloorShown = false;

  /** Link of a floor: #/etage/<its number> (web/store.js floorHash). */
  function floorLink(id) {
    return floorHash(store.get().office.floors, id);
  }

  function defaultHash() {
    const floors = sortedFloors(store.get().office.floors);
    if (!floors.length) return '#/building';
    const saved = storageGet(FLOOR_KEY);
    const f = floors.find((x) => x.id === saved) || floors[0];
    return floorLink(f.id);
  }

  function baseHash() {
    const { ui } = store.get();
    if (ui.view === 'building' || !ui.floorId) return '#/building';
    return floorLink(ui.floorId);
  }

  function showFloor(id) {
    const { ui, office } = store.get();
    if (ui.floorId === id && ui.view === 'floor' && firstFloorShown) return;
    const animate = firstFloorShown && !motionQuery.matches;
    store.setUi({ floorId: id, view: 'floor', deptFilter: ui.floorId === id ? ui.deptFilter : null, hoverId: null });
    storageSet(FLOOR_KEY, id);
    sceneCall('showFloor', id, { animate });
    firstFloorShown = true;
    const f = office.floors.find((x) => x.id === id);
    document.title = f ? `${f.name} · ${t('app.name')}` : t('app.name');
    const last = floorLoadedAt.get(id) || 0;
    if (Date.now() - last > 30000) loadTasks({ floorId: id });
  }

  function showBuilding() {
    if (store.get().ui.view === 'building' && firstFloorShown) return;
    store.setUi({ view: 'building', deptFilter: null, hoverId: null });
    sceneCall('showBuilding', { animate: firstFloorShown && !motionQuery.matches });
    firstFloorShown = true;
    document.title = `${t('elevator.building')} · ${t('app.name')}`;
  }

  /** Deep links to a panel (#/task/…) still need a floor (or the building) behind them. */
  function ensureBaseView() {
    if (firstFloorShown) return;
    const f = floorOfRoute(store.get().office.floors, parseRoute(defaultHash()));
    if (f) showFloor(f.id); else showBuilding();
  }

  function closeRouteOverlay() {
    const entry = routeOverlay;
    routeOverlay = null;
    entry?.layer?.close?.('route');
    store.setUi({ selectedTaskId: null, selectedAgentId: null });
  }

  function openRouteOverlay(r) {
    if (routeOverlay?.key === r.key) return;
    // The Studio sheet stays open while one moves between its list, an element and its tree.
    const studioRoute = r.name === 'studio' || r.name === 'studioItem';
    if (studioRoute && routeOverlay?.studio && routeOverlay.layer?.show && routeOverlay.layer.isOpen?.()) {
      routeOverlay.key = r.key;
      routeOverlay.layer.show(r);
      return;
    }
    const s = store.get();
    const isAdmin = s.me?.role === 'admin';
    if ((r.name === 'admin' || r.name === 'atelier') && !isAdmin) {
      toast.warn(t('app.adminOnly'));
      navigate(baseHash(), { replace: true });
      return;
    }
    if (r.name === 'atelier' && !s.features?.atelier) {
      toast.warn(t('app.atelierDisabled'));
      navigate(baseHash(), { replace: true });
      return;
    }
    if (r.name === 'connectors' && !libraryAccess(s).enabled) {
      toast.warn(t('app.personalDisabled'));
      navigate(baseHash(), { replace: true });
      return;
    }
    const openers = {
      task: () => panels.openTaskDrawer(ctx, r.id),
      agent: () => panels.openAgentDrawer(ctx, r.id, r.tab || undefined),
      calendar: () => panels.openCalendar(ctx),
      kanban: () => panels.openKanban(ctx),
      automations: () => panels.openAutomations(ctx, r.tab || undefined),
      approvals: () => panels.openApprovals(ctx),
      admin: () => panels.openAdmin(ctx, r.tab || undefined),
      atelier: () => panels.openAtelier(ctx),
      studio: () => panels.openStudio(ctx, r),
      studioItem: () => panels.openStudio(ctx, r),
      suggestions: () => panels.openSuggestions(ctx, r.tab || undefined),
      connectors: () => panels.openMyConnectors(ctx, r.id || undefined),
    };
    const open = openers[r.name];
    if (!open) return;
    closeRouteOverlay();
    store.setUi({ selectedTaskId: r.name === 'task' ? r.id : null, selectedAgentId: r.name === 'agent' ? r.id : null });
    const entry = { key: r.key, layer: null, studio: studioRoute };
    routeOverlay = entry;
    Promise.resolve().then(open).then((layer) => {
      if (routeOverlay !== entry || destroyed) { layer?.close?.('route'); return; }
      entry.layer = layer || null;
      layer?.onClose?.((reason) => {
        if (routeOverlay !== entry) return;
        routeOverlay = null;
        store.setUi({ selectedTaskId: null, selectedAgentId: null });
        // A task opened from the board goes back to it (#/task/<id>?from=taches), one opened from
        // Administration → Activité to that tab (?from=activite).
        const back = r.from === 'kanban' ? '#/taches' : r.from === 'activity' ? '#/admin/activity' : baseHash();
        if (reason !== 'route') navigate(back, { replace: true });
      });
    }).catch((err) => {
      console.error('[panel]', r.name, err);
      toast.error(err);
      if (routeOverlay === entry) { routeOverlay = null; navigate(baseHash(), { replace: true }); }
    });
  }

  function route() {
    if (destroyed) return;
    const r = parseRoute(location.hash);
    store.setUi({ route: r });
    const floors = store.get().office.floors;
    switch (r.name) {
      case 'floor': {
        // #/etage/<n>; an old #/floor/<id> link is redirected to the floor's number.
        const f = floorOfRoute(floors, r);
        if (!f) {
          if (floors.length) toast.warn(t('app.unknownFloor'));
          navigate(defaultHash(), { replace: true });
          return;
        }
        const canonical = floorLink(f.id);
        if (location.hash !== canonical) { navigate(canonical, { replace: true }); return; }
        closeRouteOverlay();
        showFloor(f.id);
        return;
      }
      case 'building':
        closeRouteOverlay();
        showBuilding();
        return;
      case 'studio': {
        // #/etage/<n>/studio[/<deptId>]: the floor behind the sheet is that floor.
        const f = floorOfRoute(floors, { name: 'floor', number: r.number });
        if (!f) {
          if (floors.length) toast.warn(t('app.unknownFloor'));
          navigate(defaultHash(), { replace: true });
          return;
        }
        showFloor(f.id);
        openRouteOverlay(r);
        return;
      }
      case 'home':
      case 'setup':
        navigate(defaultHash(), { replace: true });
        return;
      default:
        ensureBaseView();
        openRouteOverlay(r);
    }
  }

  const onHashChange = () => route();
  window.addEventListener('hashchange', onHashChange);
  cleanups.push(() => window.removeEventListener('hashchange', onHashChange));

  // --- keyboard -------------------------------------------------------------------------------------
  function stepFloor(delta) {
    const floors = sortedFloors(store.get().office.floors);
    if (!floors.length) return;
    const idx = floors.findIndex((f) => f.id === store.get().ui.floorId);
    const next = floors[Math.max(0, Math.min(floors.length - 1, (idx < 0 ? 0 : idx) + delta))];
    if (next) navigate(floorLink(next.id));
  }

  const keys = createKeys({
    overlay,
    handlers: {
      board: () => navigate('#/taches'),
      calendar: () => navigate('#/calendar'),
      approvals: () => navigate('#/approvals'),
      building: () => {
        const { ui } = store.get();
        if (ui.view === 'building' && ui.floorId) navigate(floorLink(ui.floorId));
        else navigate('#/building');
      },
      theme: toggleTheme,
      help: () => openShortcutsHelp(overlay),
      // PageUp = one floor up (the higher number), like the building; PageDown = one down.
      floorUp: () => stepFloor(1),
      floorDown: () => stepFloor(-1),
      floor: (i) => {
        // Key n goes to the floor numbered n (its real order), else to the n-th floor shown.
        const list = sortedFloors(store.get().office.floors);
        const f = list.find((x, idx) => floorNumber(x, idx) === i + 1) || list[i];
        if (f) navigate(floorLink(f.id));
      },
      escape: () => { if (store.get().ui.deptFilter) store.setUi({ deptFilter: null }); },
    },
  });
  ctx.keys = keys;
  cleanups.push(() => keys.destroy());

  // --- connector popover ------------------------------------------------------------------------------
  function showConnector(c, chipEl) {
    const s = store.get();
    const label = c.label || format.connectorLabel(c.name || c.key);
    // A chip stands for every entry of its service (Claude and Codex): `keys`.
    const keys = new Set(Array.isArray(c.keys) && c.keys.length ? c.keys : [c.key]);
    const deptIds = [...new Set(deptConnectorLinks({ ...s.office, connectors: s.connectors, floorId: s.ui.floorId })
      .filter((l) => keys.has(l.key)).map((l) => l.deptId))];
    const names = deptIds.map((id) => s.office.departments.find((d) => d.id === id)?.name).filter(Boolean);
    const tone = { connected: 'ok', 'needs-auth': 'warn', failed: 'error', error: 'error' }[c.status] || 'off';
    // A connector of the office's library: who added it, on every floor; « Connecteurs » shows it.
    const library = c.library === true;
    let layer = null;
    const content = h('div', { class: 'conn-pop' },
      h('p', { class: 'conn-pop-title' }, iconForConnector(library ? { key: c.catalogId || c.key, name: label } : c, { size: 22 }), h('span', {}, label)),
      h('p', { class: 'conn-pop-status' }, statusDot(tone), h('span', {}, t(`connector.status.${c.status || 'unknown'}`))),
      c.readCount != null || c.writeCount != null
        ? h('p', { class: 'conn-pop-text muted' }, t('connector.tools', { read: c.readCount ?? 0, write: c.writeCount ?? 0 })) : null,
      library
        ? h('p', { class: 'conn-pop-text muted' }, [c.addedByName ? t('connector.addedBy', { name: c.addedByName }) : '', t('connector.everyFloor')].filter(Boolean).join(' · '))
        : null,
      s.ui.view !== 'building'
        ? h('p', { class: 'conn-pop-text' }, names.length ? t('connector.usedBy', { list: names.join(', ') }) : t('connector.unused')) : null,
      library && libraryAccess(s).enabled
        ? button(t('connector.openLibrary'), {
          variant: 'secondary', size: 'sm', onClick: () => { layer?.close('select'); navigate(`#/connectors/${encodeURIComponent(c.key)}`); },
        })
        : s.me?.role === 'admin'
          ? button(t('connector.manage'), { variant: 'secondary', size: 'sm', onClick: () => { layer?.close('select'); navigate('#/admin/connectors'); } })
          : null);
    layer = overlay.popover(chipEl, content, { placement: 'bottom-center', label, initialFocus: 'panel' });
  }

  // --- store → scene ---------------------------------------------------------------------------------
  let lastOffice = null;
  offs.push(store.on('office', (office) => {
    // A renamed floor keeps its tab title in step.
    const { ui } = store.get();
    if (ui.view !== 'building' && ui.floorId && firstFloorShown) {
      const f = office.floors.find((x) => x.id === ui.floorId);
      if (f && document.title !== `${f.name} · ${t('app.name')}`) document.title = `${f.name} · ${t('app.name')}`;
    }
    if (!lastOffice || office.floors !== lastOffice.floors || office.departments !== lastOffice.departments || office.agents !== lastOffice.agents) {
      lastOffice = office;
      pushOffice();
      pushStats();
    }
    renderSceneEmpty();
  }));
  offs.push(store.on('stats', pushStats));
  offs.push(store.on('ui', (ui, state, prev) => {
    if (ui.view !== prev?.ui?.view || ui.floorId !== prev?.ui?.floorId) renderSceneEmpty();
    document.documentElement.dataset.view = ui.view;
  }));
  offs.push(store.on('degraded', (v) => { if (v) toast.warn(t('app.degraded')); }));
  // The library switched on, or this person's permission, role or policy changed: his view of the
  // library (what he may manage, what is allowed for him) is read again.
  const libraryKey = () => {
    const st = store.get();
    const a = libraryAccess(st);
    return JSON.stringify([a.enabled, a.canAdd, st.me?.id ?? null, st.me?.role ?? null, st.me?.connectorPolicy ?? null]);
  };
  let lastLibraryKey = libraryKey();
  const onLibraryKey = () => {
    const next = libraryKey();
    if (next === lastLibraryKey) return;
    lastLibraryKey = next;
    loadLibraryConnectors();
  };
  offs.push(store.on('me', onLibraryKey));
  offs.push(store.on('features', onLibraryKey));

  // --- anchors poll (scenes without onAnchors, idle periods) ------------------------------------------
  function pollAnchors() {
    if (destroyed || document.hidden) return;
    if (performance.now() - lastPushAt < 1000) return;
    let anchors = [];
    try { anchors = scene.getAnchors?.() || []; } catch { anchors = []; }
    applyAnchors(anchors);
  }
  const pollTimer = setInterval(pollAnchors, 250);
  let hiddenAt = 0;
  const onVisibility = () => {
    if (document.hidden) { hiddenAt = Date.now(); return; }
    pollAnchors();
    if (hiddenAt && Date.now() - hiddenAt > 60000) resync();
  };
  document.addEventListener('visibilitychange', onVisibility);
  cleanups.push(() => { document.removeEventListener('visibilitychange', onVisibility); clearInterval(pollTimer); });
  const statsInterval = setInterval(() => { if (!document.hidden) loadStats(); }, 60000);
  cleanups.push(() => { clearInterval(statsInterval); clearTimeout(statsTimer); clearTimeout(bootstrapTimer); });

  // --- start ------------------------------------------------------------------------------------------
  lastOffice = store.get().office;
  pushOffice();
  renderSceneEmpty();
  if (store.get().degraded) toast.warn(t('app.degraded'));
  // Insets before the first frame: the default floor is framed right away, without a jump.
  syncLayout();
  route();
  loadTasks();
  loadStats();
  loadRoutines();
  loadSuggestions();
  loadLibraryConnectors();
  scheduleLayout();
  pollAnchors();

  return {
    sceneCall,
    ctx,
    /** Positions cards, tags and lines from a list of anchors (used by the scene push; handy in harnesses). */
    applyAnchors,
    destroy() {
      destroyed = true;
      for (const off of offs) off();
      for (const fn of cleanups) { try { fn(); } catch (err) { console.error(err); } }
      overlay.closeAll('route');
      toast.destroy();
      panel.destroy();
      topbar.destroy();
      elevator.destroy();
      cards?.destroy();
      tags?.destroy();
      lines?.destroy();
      try { scene.dispose?.(); } catch (err) { console.error('[scene] dispose', err); }
      clear(root);
    },
  };

  function navigate(hash, { replace = false } = {}) {
    const target = String(hash || '#/').startsWith('#') ? String(hash) : `#${hash}`;
    if (replace) {
      history.replaceState(null, '', target);
      route();
      return;
    }
    if (location.hash === target) { route(); return; }
    location.hash = target;
  }
}

// ---------------------------------------------------------------------------------------------
// Start

(function init() {
  const pref = storageGet(THEME_KEY);
  applyTheme(pref === 'light' || pref === 'dark' ? pref : 'system');
  store.setUi({ reducedMotion: motionQuery.matches });
  document.documentElement.classList.toggle('reduced-motion', motionQuery.matches);
  boot();
}());
